/**
 * Formatação pt-BR.
 *
 * A API manda datas civis como `AAAA-MM-DD`, e a conversão acontece **sem
 * passar por `Date`**: `new Date('2026-01-31')` é interpretado como meia-noite
 * UTC e, em São Paulo, exibiria 30/01. O erro de um dia num vencimento de
 * certificado é o tipo de coisa que só aparece numa auditoria.
 */

/** `2026-01-31` → `31/01/2026`. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return iso;
  return `${match[3]}/${match[2]}/${match[1]}`;
}

/** `2026-01` → `janeiro/2026`. */
export function formatCompetence(competence: string | null | undefined): string {
  if (!competence) return '—';
  const [year, month] = competence.split('-');
  const index = Number(month) - 1;
  const names = [
    'janeiro',
    'fevereiro',
    'março',
    'abril',
    'maio',
    'junho',
    'julho',
    'agosto',
    'setembro',
    'outubro',
    'novembro',
    'dezembro',
  ];
  return names[index] ? `${names[index]}/${year}` : competence;
}

/** Instante ISO completo → `31/01/2026 às 14:05`, no fuso de São Paulo. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const formatted = new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
  return formatted.replace(', ', ' às ');
}

/** `11222333000199` → `11.222.333/0001-99`. Devolve como veio se não for CNPJ. */
export function formatCnpj(cnpj: string | null | undefined): string {
  if (!cnpj) return '—';
  const digits = cnpj.replace(/\D/g, '');
  if (digits.length !== 14) return cnpj;
  return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
}

/** Junta cidade e UF sem deixar traço solto quando falta uma das duas. */
export function formatPlace(city: string | null, state: string | null): string {
  if (city && state) return `${city}/${state}`;
  return city ?? state ?? '—';
}

/**
 * Índice em pt-BR: `96,88%`. `null` é **N/A**, nunca `0%` — a diferença entre
 * "não teve entrega no mês" e "entregou tudo atrasado" é a razão de o campo
 * poder ser nulo.
 */
export function formatIndex(value: number | null | undefined): string {
  if (value === null || value === undefined) return 'N/A';
  return `${value.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;
}

/** Número com duas casas, sem o sinal de porcentagem. */
export function formatDecimal(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Quantidade de peças.
 *
 * Sem casas fixas: o campo aceita fração (peso, metro), mas contagem de peça
 * é inteira — e "1.200,0000 não conformes" faz uma contagem parecer medição.
 */
export function formatQuantity(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString('pt-BR', { maximumFractionDigits: 4 });
}
